import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { I } from '../Icons'
import WipAiEmptyState from './WipAiEmptyState'
import WipAiDiffCard from './WipAiDiffCard'

const Aside = styled.aside`
  position: relative;
  top: 52px;
  height: calc(100vh - 52px);
  border-left: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const SbResizer = styled.div`
  position: absolute;
  left: -3px;
  top: 0;
  bottom: 0;
  width: 6px;
  cursor: col-resize;
  z-index: 20;
  &:hover {
    background: linear-gradient(
      90deg,
      transparent,
      ${p => p.theme.colors.lilac.light},
      transparent
    );
  }
`

const SbRail = styled.div`
  display: ${p => (p.$show ? 'flex' : 'none')};
  flex-direction: column;
  gap: 10px;
  align-items: center;
  padding-top: 8px;
  width: 100%;
`

const Rb = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  background: ${p => p.theme.colors.semantic.surface};
  display: grid;
  place-items: center;
  color: ${p => p.theme.colors.semantic.textMuted};
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
    color: ${p => p.theme.colors.semantic.textPrimary};
  }
`

const RbPrimary = styled(Rb)`
  background: ${p => p.theme.colors.semantic.accent};
  color: #fff;
  border-color: ${p => p.theme.colors.semantic.accent};
`

const SbFull = styled.div`
  display: ${p => (p.$hidden ? 'none' : 'flex')};
  flex-direction: column;
  height: 100%;
  min-height: 0;
  width: 100%;
`

const SbHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  flex-shrink: 0;
`

const Tag = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  background: ${p => p.theme.colors.black};
  color: #fff;
  display: grid;
  place-items: center;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-weight: 700;
  font-size: 11px;
`

const Info = styled.div`
  flex: 1;
  min-width: 0;
`

const InfoT = styled.div`
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const InfoS = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.semantic.textMuted};
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
`

const IconBtn = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  display: grid;
  place-items: center;
  color: ${p => p.theme.colors.semantic.textMuted};
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
    color: ${p => p.theme.colors.semantic.textPrimary};
  }
`

const SbRevs = styled.div`
  padding: 10px 12px;
  border-bottom: 1px solid ${p => p.theme.colors.semantic.border};
  flex-shrink: 0;
  max-height: 150px;
  overflow-y: auto;
`

const RevsH = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${p => p.theme.colors.semantic.textMuted};
  padding: 2px 4px 8px;
`

const RevItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.12s;
  background: ${p => (p.$active ? p.theme.colors.semantic.lilacTint : 'transparent')};
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
  }
`

const Src = styled.div`
  width: 24px;
  height: 24px;
  border-radius: 6px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 9px;
  font-weight: 700;
  background: ${p => p.$bg};
  color: ${p => p.$fg};
`

const RevMeta = styled.div`
  flex: 1;
  min-width: 0;
`

const RevN = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 12px;
  font-weight: 700;
  color: ${p => (p.$active ? p.theme.colors.semantic.accent : p.theme.colors.semantic.textPrimary)};
`

const RevC = styled.div`
  font-size: 11px;
  color: ${p => p.theme.colors.semantic.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const RevTime = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  color: ${p => p.theme.colors.semantic.textFaint};
`

const SbMessages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-height: 0;
`

const Msg = styled.div`
  display: flex;
  gap: 10px;
  max-width: 100%;
  flex-direction: ${p => (p.$user ? 'row-reverse' : 'row')};
`

const Av = styled.div`
  width: 26px;
  height: 26px;
  border-radius: 7px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  margin-top: 2px;
  background: ${p => (p.$user ? p.theme.colors.semantic.lilacTint : p.theme.colors.black)};
  color: ${p => (p.$user ? p.theme.colors.semantic.accent : '#fff')};
`

const Body = styled.div`
  min-width: 0;
  flex: 1;
  max-width: calc(100% - 36px);
`

const Who = styled.div`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  font-weight: 700;
  color: ${p => p.theme.colors.semantic.textMuted};
  margin-bottom: 4px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: ${p => (p.$user ? 'right' : 'left')};
`

const Bub = styled.div`
  background: ${p => p.theme.colors.semantic.surface};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 10px;
  padding: 11px 13px;
  font-size: 13px;
  line-height: 1.55;
  border-left: ${p => (!p.$user ? `3px solid ${p.theme.colors.semantic.accent}` : 'none')};
  border-top-left-radius: ${p => (!p.$user ? '3px' : '10px')};
  background: ${p =>
    p.$user ? p.theme.colors.semantic.surfaceRaised : p.theme.colors.semantic.surface};
  p {
    margin: 0 0 8px;
  }
  p:last-child {
    margin-bottom: 0;
  }
`

const Cites = styled.div`
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
  margin-top: 8px;
`

const CiteChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  font-weight: 600;
  background: ${p => p.theme.colors.semantic.surfaceMuted};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  color: ${p => p.theme.colors.semantic.textMuted};
  padding: 2px 7px;
  border-radius: 4px;
  cursor: help;
  &:hover {
    background: ${p => p.theme.colors.semantic.lilacTint};
    color: ${p => p.theme.colors.semantic.accent};
    border-color: ${p => p.theme.colors.semantic.lilacLight};
  }
`

const Findings = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const Finding = styled.div`
  display: flex;
  gap: 8px;
  padding: 8px 10px;
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 8px;
  background: ${p => p.theme.colors.semantic.surfaceRaised};
`

const FindingSev = styled.div`
  width: 4px;
  border-radius: 2px;
  flex-shrink: 0;
  background: ${p => p.$c};
`

const Typing = styled.span`
  display: inline-flex;
  gap: 3px;
  align-items: center;
  margin-left: 8px;
  span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: ${p => p.theme.colors.semantic.textMuted};
    animation: wipAiTyping 1.2s infinite;
  }
  span:nth-child(2) {
    animation-delay: 0.2s;
  }
  span:nth-child(3) {
    animation-delay: 0.4s;
  }
  @keyframes wipAiTyping {
    0%,
    60%,
    100% {
      opacity: 0.2;
    }
    30% {
      opacity: 1;
    }
  }
`

const SbComposer = styled.div`
  padding: 10px 12px 12px;
  border-top: 1px solid ${p => p.theme.colors.semantic.border};
  flex-shrink: 0;
  background: ${p => p.theme.colors.semantic.surface};
`

const Field = styled.div`
  display: flex;
  gap: 6px;
  align-items: flex-end;
  background: ${p => p.theme.colors.semantic.surfaceRaised};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  border-radius: 10px;
  padding: 6px 6px 6px 12px;
  &:focus-within {
    border-color: ${p => p.theme.colors.semantic.accent};
    background: ${p => p.theme.colors.semantic.surface};
  }
`

const Ta = styled.textarea`
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  resize: none;
  font-size: 13px;
  line-height: 1.45;
  min-height: 24px;
  max-height: 160px;
  padding: 6px 0;
`

const Tools = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const ToolBtn = styled.button`
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: none;
  background: transparent;
  display: grid;
  place-items: center;
  color: ${p => p.theme.colors.semantic.textMuted};
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.semantic.surfaceMuted};
    color: ${p => p.theme.colors.semantic.textPrimary};
  }
`

const SendBtn = styled(ToolBtn)`
  background: ${p => p.theme.colors.semantic.accent};
  color: #fff;
  &:hover {
    background: ${p => p.theme.colors.semantic.accentHover};
    color: #fff;
  }
  &:disabled {
    background: ${p => p.theme.colors.semantic.borderStrong};
    cursor: default;
  }
`

const Slash = styled.div`
  display: flex;
  gap: 4px;
  margin-top: 7px;
  flex-wrap: wrap;
`

const SlashBtn = styled.button`
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  font-size: 10px;
  background: ${p => p.theme.colors.semantic.surfaceMuted};
  border: 1px solid ${p => p.theme.colors.semantic.border};
  color: ${p => p.theme.colors.semantic.textMuted};
  padding: 3px 7px;
  border-radius: 4px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: ${p => p.theme.colors.semantic.lilacTint};
    color: ${p => p.theme.colors.semantic.accent};
    border-color: ${p => p.theme.colors.semantic.lilacLight};
  }
`

const Hint = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 10px;
  color: ${p => p.theme.colors.semantic.textFaint};
  margin-top: 6px;
  padding: 0 4px;
  font-family: ${p => p.theme.fontFamily.sans.join(', ')};
  letter-spacing: 0.04em;
`

function renderMd(text) {
  const out = (text || '')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`([^`]+)`/g, '<code>$1</code>')
  return { __html: out }
}

function sevColor(theme, sev) {
  if (sev === 'critical') return theme.colors.semantic.danger
  if (sev === 'warn') return theme.colors.semantic.warn
  return theme.colors.semantic.accent
}

const WipAiSidebarShell = ({
  collapsed,
  onToggleCollapsed,
  conversation = null,
  revisions,
  selectedRevId,
  onSelectRev,
  onGenerateSummary,
  onSendMessage,
  onSubmitRevision,
  onDiscardProposal,
  onNewThread,
  generating,
  onCiteHover,
  onResizerMouseDown,
  theme,
}) => {
  const msgListRef = useRef(null)
  const [input, setInput] = useState('')

  useEffect(() => {
    if (msgListRef.current) msgListRef.current.scrollTop = msgListRef.current.scrollHeight
  }, [conversation?.messages?.length, generating])

  const handleSend = () => {
    if (!input.trim() || generating) return
    onSendMessage(input.trim())
    setInput('')
  }

  if (collapsed) {
    return (
      <Aside>
        <SbRail $show>
          <RbPrimary type="button" title="Expand assistant" onClick={onToggleCollapsed}>
            <I.sparkle width={16} height={16} />
          </RbPrimary>
          <Rb type="button" title="Revisions">
            <I.file width={15} height={15} />
          </Rb>
          <Rb type="button" title="New thread" onClick={onNewThread}>
            <I.plus width={16} height={16} />
          </Rb>
        </SbRail>
      </Aside>
    )
  }

  return (
    <Aside>
      <SbResizer onMouseDown={onResizerMouseDown} />
      <SbFull $hidden={false}>
        <SbHead>
          <Tag>AI</Tag>
          <Info>
            <InfoT>WIP Report 2026-04</InfoT>
            <InfoS>
              {revisions.length} revision{revisions.length === 1 ? '' : 's'} ·{' '}
              {conversation?.messages?.length || 0} messages
            </InfoS>
          </Info>
          <IconBtn type="button" title="New thread" onClick={onNewThread}>
            <I.plus width={15} height={15} />
          </IconBtn>
          <IconBtn
            type="button"
            title="Collapse"
            onClick={onToggleCollapsed}
            data-testid="wip-ai-collapse-sidebar"
          >
            <I.panelL width={15} height={15} />
          </IconBtn>
          <IconBtn type="button" title="More">
            <I.kebab width={15} height={15} />
          </IconBtn>
        </SbHead>

        {revisions.length > 0 && (
          <SbRevs>
            <RevsH>Revisions</RevsH>
            {revisions.map(r => (
              <RevItem
                key={r.id}
                $active={r.id === selectedRevId}
                onClick={() => onSelectRev(r.id)}
              >
                <Src
                  $bg={r.source === 'ai' ? '#050519' : r.source === 'user' ? '#EEEEFF' : '#4241BF'}
                  $fg={r.source === 'ai' ? '#fff' : r.source === 'user' ? '#4241BF' : '#fff'}
                >
                  {r.source === 'ai' ? 'AI' : r.source === 'user' ? 'YOU' : 'AI+'}
                </Src>
                <RevMeta>
                  <RevN $active={r.id === selectedRevId}>Rev {r.revisionNumber}</RevN>
                  <RevC>{r.caption}</RevC>
                </RevMeta>
                <RevTime>{r.timeLabel}</RevTime>
              </RevItem>
            ))}
          </SbRevs>
        )}

        <SbMessages ref={msgListRef}>
          {(!conversation || conversation.messages.length === 0) && !generating && (
            <WipAiEmptyState onGenerate={onGenerateSummary} disabled={generating} />
          )}

          {conversation?.messages?.map((m, i) => {
            if (m.role === 'system') {
              return (
                <div key={m.id} style={{ fontSize: 12, color: '#676767', textAlign: 'center' }}>
                  {m.content}
                </div>
              )
            }
            const isUser = m.role === 'user'
            const paragraphs = (m.content || '').split('\n\n').filter(Boolean)

            return (
              <Msg key={m.id} $user={isUser}>
                <Av $user={isUser}>
                  {isUser ? (
                    <I.user width={13} height={13} />
                  ) : (
                    <I.sparkle width={12} height={12} />
                  )}
                </Av>
                <Body>
                  <Who $user={isUser}>{isUser ? 'You' : 'Assistant'}</Who>
                  <Bub $user={isUser}>
                    {paragraphs.map((para, j) => (
                      <p key={j} dangerouslySetInnerHTML={renderMd(para)} />
                    ))}
                    {m.findings && m.findings.length > 0 && (
                      <Findings>
                        {m.findings.slice(0, 3).map(f => (
                          <Finding key={f.id}>
                            <FindingSev $c={sevColor(theme, f.severity)} />
                            <div className="txt" style={{ fontSize: 12, lineHeight: 1.5 }}>
                              <span style={{ fontWeight: 600 }}>{f.title}.</span>{' '}
                              <span style={{ color: '#676767' }}>{f.detail.split('.')[0]}.</span>
                            </div>
                          </Finding>
                        ))}
                        {m.findings.length > 3 && (
                          <div style={{ fontSize: 11, color: '#676767', paddingLeft: 12 }}>
                            + {m.findings.length - 3} more
                          </div>
                        )}
                      </Findings>
                    )}
                    {m.citations && m.citations.length > 0 && (
                      <Cites>
                        {m.citations.map((c, k) => (
                          <CiteChip
                            key={k}
                            onMouseEnter={() => onCiteHover(c.path)}
                            onMouseLeave={() => onCiteHover(null)}
                          >
                            <I.hash width={9} height={9} />{' '}
                            {c.path
                              .split('.')
                              .pop()
                              .replace(/\[.*\]/, '')}
                          </CiteChip>
                        ))}
                      </Cites>
                    )}
                  </Bub>
                  {m.proposedChanges && (
                    <WipAiDiffCard
                      changes={m.proposedChanges}
                      producedRev={m.producedRevisionId}
                      nextRevNumber={revisions.length + 1}
                      onSubmit={() => onSubmitRevision(m.id)}
                      onDiscard={() => onDiscardProposal(m.id)}
                      submitted={!!m.producedRevisionId}
                      producedRevLabel={m.producedRevLabel}
                    />
                  )}
                </Body>
              </Msg>
            )
          })}

          {generating && (
            <Msg $user={false}>
              <Av $user={false}>
                <I.sparkle width={12} height={12} />
              </Av>
              <Body>
                <Who $user={false}>Assistant</Who>
                <Bub $user={false}>
                  <span style={{ color: '#676767', fontSize: 12 }}>Analyzing April 2026 data</span>
                  <Typing>
                    <span />
                    <span />
                    <span />
                  </Typing>
                </Bub>
              </Body>
            </Msg>
          )}
        </SbMessages>

        <SbComposer>
          <Field>
            <Ta
              placeholder="Ask the assistant…"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault()
                  handleSend()
                }
              }}
              rows={1}
            />
            <Tools>
              <ToolBtn type="button" title="Attach">
                <I.plus width={15} height={15} />
              </ToolBtn>
              <SendBtn
                type="button"
                onClick={handleSend}
                disabled={!input.trim() || generating}
                title="Send"
              >
                <I.send width={14} height={14} />
              </SendBtn>
            </Tools>
          </Field>
          <Slash>
            <SlashBtn type="button" onClick={() => setInput('/summary ')}>
              /summary
            </SlashBtn>
            <SlashBtn type="button" onClick={() => setInput('/check ')}>
              /check
            </SlashBtn>
            <SlashBtn type="button" onClick={() => setInput('/compare rev 1 ')}>
              /compare
            </SlashBtn>
            <SlashBtn type="button" onClick={() => setInput('/email ')}>
              /email
            </SlashBtn>
          </Slash>
          <Hint>
            <span>⏎ Send · ⇧⏎ Newline</span>
            <span>Cmd+. toggle</span>
          </Hint>
        </SbComposer>
      </SbFull>
    </Aside>
  )
}

WipAiSidebarShell.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  onToggleCollapsed: PropTypes.func.isRequired,
  conversation: PropTypes.object,
  revisions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedRevId: PropTypes.string.isRequired,
  onSelectRev: PropTypes.func.isRequired,
  onGenerateSummary: PropTypes.func.isRequired,
  onSendMessage: PropTypes.func.isRequired,
  onSubmitRevision: PropTypes.func.isRequired,
  onDiscardProposal: PropTypes.func.isRequired,
  onNewThread: PropTypes.func.isRequired,
  generating: PropTypes.bool.isRequired,
  onCiteHover: PropTypes.func.isRequired,
  onResizerMouseDown: PropTypes.func.isRequired,
  theme: PropTypes.object.isRequired,
}

export default WipAiSidebarShell
